import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { useHabitAnalytics, HabitDayData } from "@/hooks/use-habit-analytics";
import { cn } from "@/lib/utils";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO, addMonths, subMonths } from "date-fns";

interface HabitCalendarViewProps {
  clientId: string;
  className?: string;
}

function getCompletionColor(rate: number): string {
  if (rate === 100) return "bg-success";
  if (rate >= 75) return "bg-success/80";
  if (rate >= 50) return "bg-warning";
  if (rate >= 25) return "bg-warning/80";
  if (rate > 0) return "bg-destructive/80";
  return "bg-muted";
}

function getCompletionBgColor(rate: number): string {
  if (rate === 100) return "bg-success/15 hover:bg-success/25";
  if (rate >= 75) return "bg-success/10 hover:bg-success/15";
  if (rate >= 50) return "bg-warning/10 hover:bg-warning/15";
  if (rate >= 25) return "bg-warning/10 hover:bg-warning/15";
  if (rate > 0) return "bg-destructive/10 hover:bg-destructive/15";
  return "hover:bg-muted/50";
}

interface DayCellProps {
  date: Date;
  dayData: HabitDayData | undefined;
  isCurrentMonth: boolean;
}

function DayCell({ date, dayData, isCurrentMonth }: DayCellProps) {
  const dateStr = format(date, "yyyy-MM-dd");
  const dayNum = format(date, "d");
  const hasData = dayData && dayData.totalHabits > 0;
  const completionRate = dayData?.completionRate || 0;

  if (!isCurrentMonth) {
    return (
      <div className="h-10 w-10 flex items-center justify-center text-muted-foreground/30 text-sm">
        {dayNum}
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        className={cn(
          "h-10 w-10 flex items-center justify-center text-sm rounded-lg transition-colors",
          isToday(date) && "ring-2 ring-primary ring-offset-1",
          "text-muted-foreground"
        )}
      >
        {dayNum}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "h-10 w-10 flex flex-col items-center justify-center text-sm rounded-lg cursor-pointer transition-colors relative",
              getCompletionBgColor(completionRate),
              isToday(date) && "ring-2 ring-primary ring-offset-1"
            )}
          >
            <span className="font-medium">{dayNum}</span>
            <div
              className={cn(
                "absolute bottom-1 h-1 w-4 rounded-full",
                getCompletionColor(completionRate)
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-2">
            <div className="font-medium">{format(date, "EEEE, MMMM d")}</div>
            <div className="text-sm">
              <span className="font-semibold text-success">
                {dayData.completedHabits}
              </span>
              <span className="text-muted-foreground">
                /{dayData.totalHabits} habits ({completionRate}%)
              </span>
            </div>
            <div className="space-y-1">
              {dayData.habits.slice(0, 5).map((habit, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                  {habit.completed ? (
                    <CheckCircle2 className="h-3 w-3 text-success" />
                  ) : (
                    <XCircle className="h-3 w-3 text-destructive" />
                  )}
                  <span className={habit.completed ? "" : "text-muted-foreground"}>
                    {habit.habitName}
                  </span>
                </div>
              ))}
              {dayData.habits.length > 5 && (
                <div className="text-xs text-muted-foreground">
                  +{dayData.habits.length - 5} more
                </div>
              )}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function HabitCalendarView({ clientId, className }: HabitCalendarViewProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const { analytics, loading } = useHabitAnalytics(clientId, 90); // Fetch 90 days for calendar

  const calendarDataMap = useMemo(() => {
    const map = new Map<string, HabitDayData>();
    if (analytics?.calendarData) {
      for (const day of analytics.calendarData) {
        map.set(day.date, day);
      }
    }
    return map;
  }, [analytics?.calendarData]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start of month to align with weekday
  const startPadding = monthStart.getDay();
  const paddedDays = [
    ...Array(startPadding).fill(null),
    ...days,
  ];

  // Calculate month stats
  const monthStats = useMemo(() => {
    let totalHabits = 0;
    let completedHabits = 0;
    let daysWithData = 0;

    for (const day of days) {
      const dateStr = format(day, "yyyy-MM-dd");
      const dayData = calendarDataMap.get(dateStr);
      if (dayData && dayData.totalHabits > 0) {
        totalHabits += dayData.totalHabits;
        completedHabits += dayData.completedHabits;
        daysWithData++;
      }
    }

    return {
      totalHabits,
      completedHabits,
      daysWithData,
      completionRate: totalHabits > 0 ? Math.round((completedHabits / totalHabits) * 100) : 0,
    };
  }, [days, calendarDataMap]);

  if (loading) {
    return (
      <Card className={cn("", className)}>
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="chart-height-lg w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5 text-primary" />
            Habit Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium min-w-[140px] text-center">
              {format(currentMonth, "MMMM yyyy")}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Month Stats */}
        <div className="flex items-center gap-4 mb-4 text-sm">
          <Badge variant="secondary" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            {monthStats.completedHabits}/{monthStats.totalHabits} completed
          </Badge>
          <Badge variant="outline" className={cn(
            monthStats.completionRate >= 75 ? "text-success border-success/30" :
            monthStats.completionRate >= 50 ? "text-warning border-warning/30" :
            "text-destructive border-destructive/30"
          )}>
            {monthStats.completionRate}% compliance
          </Badge>
          <span className="text-muted-foreground">
            {monthStats.daysWithData} active days
          </span>
        </div>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="h-8 flex items-center justify-center text-xs font-medium text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {paddedDays.map((day, idx) => {
            if (!day) {
              return <div key={`pad-${idx}`} className="h-10 w-10" />;
            }
            const dateStr = format(day, "yyyy-MM-dd");
            const dayData = calendarDataMap.get(dateStr);
            return (
              <DayCell
                key={dateStr}
                date={day}
                dayData={dayData}
                isCurrentMonth={isSameMonth(day, currentMonth)}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded bg-muted" />
            <span>No data</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded bg-destructive/80" />
            <span>&lt;25%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded bg-warning" />
            <span>50%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded bg-success/80" />
            <span>75%+</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 rounded bg-success" />
            <span>100%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
